import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { useEffect, useState } from 'react';
import { StripeCardCvcElementOptions } from '@stripe/stripe-js';
import { CardCvcElement } from '@stripe/react-stripe-js';

import { Loader } from 'shared/components/Loader';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Payment.CvvVerification';

const cvcElementOptions: StripeCardCvcElementOptions = {
  placeholder: '',
  style: {
    base: {
      fontSize: '12px',
      color: '#000000',
      lineHeight: '26px',
    },
  },
};

const CreditCardVerification = () => {
  const [isEmpty, setIsEmpty] = useState(true);
  const [showExplanation, setShowExplanation] = useState(false);
  const [cvcIsFocused, setCvcIsFocused] = useState(false);
  const { billingInfos, order, verifyCreditCard, creditCardVerificationLoading } =
    useCheckoutContext();
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);

  const {
    billingInfoId,
    id: orderNumber,
    isInstantShip,
    localizedFinalPriceCents: { currency, amount: price },
    localizedListPriceCents: { amount: lowestPrice },
    localizedShippingCents: { amount: shippingPrice },
    product,
    shippingServiceLevel: shippingMethod,
    verificationNeeded,
    wantId,
  } = order;

  const isAmex = billingInfos[billingInfoId]?.cardBrand === 'American Express';
  const underRetail =
    lowestPrice < product?.productTemplate?.localizedSpecialDisplayPriceCents?.amount;
  const isOffer = wantId && isCheckoutOffersEnabled;

  useEffect(() => {
    if (verificationNeeded) {
      sendTrackingEvent('cvv_re_entry', {
        order_id: orderNumber,
        product_template_slug: isOffer ? product?.productTemplate?.slug : product?.slug,
        size: product?.size,
        price,
        instant_ship: isInstantShip,
        instant_ship_available: product?.instantShippable,
        lowest_price: lowestPrice,
        goat_storage: false,
        under_retail: underRetail,
        shipping_price: shippingPrice,
        currency_code: currency,
        shipping_method: shippingMethod,
      });
    }
  }, [
    currency,
    isInstantShip,
    isOffer,
    lowestPrice,
    orderNumber,
    price,
    product?.instantShippable,
    product?.productTemplate?.slug,
    product?.size,
    product?.slug,
    shippingMethod,
    shippingPrice,
    underRetail,
    verificationNeeded,
  ]);

  const submitVerification = async () => {
    sendTrackingEvent('cvv_submit_tap', {
      order_id: orderNumber,
      product_template_slug: product?.slug,
      size: product?.size,
      price,
      instant_ship: isInstantShip,
      instant_ship_available: product?.instantShippable,
      goat_storage: false,
      under_retail: underRetail,
      shipping_price: shippingPrice,
      currency_code: currency,
      shipping_method: shippingMethod,
    });
    await verifyCreditCard();
  };

  if (verificationNeeded === false) {
    return null;
  }

  return (
    <CreditCardVerification.Wrapper>
      <CreditCardVerification.CardCvcWrapper $isFocused={cvcIsFocused}>
        <CreditCardVerification.Label aboveInput={!isEmpty} data-qa="checkout_cvv_header">
          {t(`${TP}.verifyCreditCardCvvLabel`, 'Verify Your CVV')}
        </CreditCardVerification.Label>
        <CardCvcElement
          data-qa="checkout_cvv_input"
          onChange={({ empty }) => {
            setIsEmpty(empty);
          }}
          onFocus={() => setCvcIsFocused(true)}
          onBlur={() => setCvcIsFocused(false)}
          options={cvcElementOptions}
        />
        {creditCardVerificationLoading ? (
          <Loader style={{ width: '30px', marginTop: '18px' }} data-qa="checkout_cvv_loader" />
        ) : (
          <CreditCardVerification.SubmitButton
            disabled={creditCardVerificationLoading || isEmpty}
            data-qa="checkout_cvv_submit"
            onClick={submitVerification}
          >
            {t(`${TP}.submitButton`, 'Submit')}
          </CreditCardVerification.SubmitButton>
        )}
      </CreditCardVerification.CardCvcWrapper>
      <CreditCardVerification.ExplanationButton
        data-qa="checkout_cvv_explanation_button"
        onFocus={() => setShowExplanation(true)}
        onMouseOver={() => setShowExplanation(true)}
        onMouseLeave={() => setShowExplanation(false)}
      >
        <CreditCardVerification.CVV>
          <Trans
            i18nKey={`${TP}.cvvVerificationDetails`}
            defaults="This is required to help prevent unauthorized use of your account. <emphasis>What is my CVV?</emphasis>"
            components={{ emphasis: <u /> }}
          />
        </CreditCardVerification.CVV>

        {showExplanation && (
          <CreditCardVerification.Explanation data-qa="checkout_cvv_explanation">
            {t(
              `${TP}.cvvExplaination`,
              'Your CVV is the {numDigits}-digit code that can be found on the back of your card.',
              {
                numDigits: isAmex ? 4 : 3,
              },
            )}
          </CreditCardVerification.Explanation>
        )}
      </CreditCardVerification.ExplanationButton>
    </CreditCardVerification.Wrapper>
  );
};

CreditCardVerification.Wrapper = styled.div`
  position: relative;
  border-bottom: 1px solid black;
  margin: 16px 0;
`;
CreditCardVerification.CardCvcWrapper = styled.div<{ $isFocused: boolean }>`
  position: relative;
  display: flex;
  width: 225px;
  padding: 7px 0;
  background-color: white;
  border-bottom: 1px solid black;
  border-bottom: ${({ $isFocused }) => ($isFocused ? '2px solid black' : '1px solid black')};

  .StripeElement {
    flex: 1;
  }
`;

CreditCardVerification.Label = styled.label<{ aboveInput: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 30;
  pointer-events: none;
  font-size: ${({ aboveInput }) => (aboveInput ? '9px' : '11px')};
  line-height: ${({ aboveInput }) => (aboveInput ? '11px' : '42px')};
  transition: all 150ms;
`;
CreditCardVerification.SubmitButton = styled.button`
  text-decoration: underline;
  padding: 0;
  &:disabled {
    color: white;
  }
`;
CreditCardVerification.ExplanationButton = styled.button`
  color: ${({ theme }) => theme.colors.red};
  position: relative;
  padding-left: 0;
  text-align: left;
  max-width: 250px;
  margin: 16px 0;

  em {
    text-decoration: underline;
    font-style: normal;
  }
`;
CreditCardVerification.Explanation = styled.div`
  position: absolute;
  top: 0;
  left: 100px;
  background-color: white;
  min-width: 275px;
  text-align: left;
  border: 1px solid black;
  padding: 12px;
  font-size: 11.5px;
  line-height: 14px;
`;

CreditCardVerification.CVV = styled.p`
  line-height: 14.5px;
  font-size: 11px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '11.5px', height: '14.8px' })};
  }
`;

export default CreditCardVerification;
